import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full px-3 py-2 border-2 border-[#3D2008] bg-white font-manrope text-sm text-[#3D2008] focus:outline-none focus:border-[#C4522A]";
const labelCls = "font-manrope text-xs font-bold text-[#3D2008] uppercase tracking-wide block mb-1";
const METHODS = ["cash", "check", "stripe", "merchandise", "other"];

export const isOnline = (d) => !String(d.session_id || "").startsWith("manual_");

// In-page modal to edit any donation record (manual or online)
export const EditDonationModal = ({ donation, adminKey, onClose, onSaved }) => {
    const [form, setForm] = useState({
        donor_name: donation.donor_name || "", donor_email: donation.donor_email || "",
        amount: donation.amount ?? "", method: donation.method || donation.package_id || "stripe",
        created_at: (donation.created_at || "").slice(0, 10), note: donation.note || "",
    });
    const [busy, setBusy] = useState(false);
    const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await axios.put(`${API}/admin/donations/${donation.session_id}`, {
                ...form, amount: parseFloat(form.amount),
                created_at: form.created_at ? `${form.created_at}T12:00:00+00:00` : undefined,
            }, { headers: { "x-admin-password": adminKey } });
            toast.success("Donation updated.");
            onSaved();
        } catch (err) { toast.error(err.response?.data?.detail || "Could not save changes."); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="edit-donation-modal">
            <form onSubmit={save} onClick={e => e.stopPropagation()} className="bg-[#F5EDD6] border-2 border-[#3D2008] w-full sm:max-w-lg p-5 sm:p-6 max-h-[92vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <h3 className="font-anton text-xl text-[#3D2008] uppercase">Edit Donation</h3>
                    <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 flex items-center justify-center text-[#3D2008] hover:bg-[#3D2008]/10"><X size={18} /></button>
                </div>
                {isOnline(donation) && (
                    <p className="flex items-start gap-2 bg-amber-100 border-2 border-amber-500 text-amber-900 font-manrope text-xs p-3 mb-4" data-testid="edit-online-warning">
                        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> This is an online (Stripe) gift. Edits change what the website shows — not Stripe's own record.
                    </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><label className={labelCls}>Donor name</label><input value={form.donor_name} onChange={f("donor_name")} data-testid="edit-donor-name" className={inputCls} /></div>
                    <div><label className={labelCls}>Email</label><input type="email" value={form.donor_email} onChange={f("donor_email")} data-testid="edit-donor-email" className={inputCls} /></div>
                    <div><label className={labelCls}>Amount ($)</label><input type="number" min="0.01" step="0.01" required value={form.amount} onChange={f("amount")} data-testid="edit-amount" className={inputCls} /></div>
                    <div><label className={labelCls}>Method</label>
                        <select value={form.method} onChange={f("method")} data-testid="edit-method" className={inputCls}>{METHODS.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
                    <div><label className={labelCls}>Date</label><input type="date" value={form.created_at} max={new Date().toISOString().slice(0, 10)} onChange={f("created_at")} data-testid="edit-date" className={inputCls} /></div>
                    <div className="sm:col-span-2"><label className={labelCls}>Note</label><input value={form.note} onChange={f("note")} maxLength={1000} data-testid="edit-note" className={inputCls} /></div>
                </div>
                <div className="flex justify-end gap-3 mt-5">
                    <button type="button" onClick={onClose} className="h-11 px-4 font-manrope text-sm text-[#5C4A35] underline">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="edit-save-btn" className="h-11 px-6 bg-[#C4522A] text-white font-anton uppercase tracking-wider border-2 border-[#3D2008] disabled:opacity-60">{busy ? "Saving…" : "Save changes"}</button>
                </div>
            </form>
        </div>
    );
};

// Delete flow: single confirm for manual gifts; online gifts require typing DELETE
export const confirmDeleteDonation = (d) => {
    const who = `${d.donor_name || "Anonymous"} — $${Number(d.amount || 0).toFixed(2)} on ${new Date(d.created_at).toLocaleDateString()}`;
    if (!isOnline(d)) return window.confirm(`Delete this donation?\n\n${who}`);
    const typed = window.prompt(`This is an ONLINE (Stripe) gift:\n\n${who}\n\nDeleting removes it from the website totals permanently (Stripe's record is untouched). Type DELETE to confirm.`);
    return typed === "DELETE";
};
